/**
 * Projektörsleverans (Dok 19 + paket).
 *
 * Samlar projektörens underlag – arbetsområdesutredning (Dok 10),
 * projektörsinlämningar (Dok 16), risksammanställningar (Dok 30b/31) och
 * arbetsmiljörutinen (Dok 18) – och paketerar allt som en ZIP som kan lämnas
 * till ett projekt som inte använder systemet.
 */
import JSZip from "jszip";
import { supabase } from "@/integrations/supabase/client";
import {
  beginExportCapture,
  endExportCapture,
  type CapturedExportFile,
} from "@/lib/documentGenerator/exportHistory";
import { generateDesignerRoutineWordDocument } from "@/lib/documentGenerator/designerRoutineWordGenerator";
import {
  generateDesignerCertificateWordDocument,
  type DesignerCertificateContent,
  type DesignerCertificateCounts,
  type DesignerCertificateDoc,
} from "@/lib/documentGenerator/designerCertificateWordGenerator";
import {
  generateDesignerSubmissionWordDocument,
  generateWorkAreaSurveyWordDocument,
} from "@/lib/documentGenerator/designerDocsWordGenerator";
import { generateRisksammanstallningWord } from "@/lib/documentGenerator/risksammanstallningWordGenerator";
import { getAllQuestionsForExport } from "@/lib/documentGenerator/utils/exportQuestionBuilder";
import { fragorData } from "@/lib/fragorData";
import { DESIGNER_ROUTINE_SECTIONS } from "@/lib/basProcess/designerRoutine";
import type { RiskbedomningData } from "@/types/riskbedomning";

export interface DesignerDeliveryData {
  projectName: string | null;
  routine: {
    name: string;
    version: string;
    status: string;
    sections: Record<string, string>;
  } | null;
  surveys: Record<string, any>[];
  surveyItems: Record<string, any>[];
  submissions: Record<string, any>[];
  riskConstruction: Record<string, any>[];
  riskUse: Record<string, any>[];
  deferrals: number;
}

const rows = async (tableName: string, projectId: string) => {
  const { data } = await supabase
    .from(tableName as never)
    .select("*")
    .eq("project_id", projectId)
    .limit(1000);
  return (data as unknown as Record<string, any>[]) || [];
};

export const collectDesignerDelivery = async (
  projectId: string
): Promise<DesignerDeliveryData> => {
  const [projectRes, routineRes, surveys, submissions, risks, deferralsRes] =
    await Promise.all([
      supabase.from("projects").select("name").eq("id", projectId).maybeSingle(),
      supabase
        .from("designer_routines")
        .select("name, version, status, sections")
        .eq("project_id", projectId)
        .order("created_at", { ascending: true })
        .maybeSingle(),
      rows("work_area_surveys", projectId),
      rows("designer_submissions", projectId),
      rows("folder_risk_assessments", projectId),
      supabase
        .from("question_deferrals")
        .select("id", { count: "exact", head: true })
        .eq("project_id", projectId),
    ]);

  let surveyItems: Record<string, any>[] = [];
  if (surveys.length) {
    const { data } = await supabase
      .from("work_area_survey_items")
      .select("*")
      .in(
        "survey_id",
        surveys.map((s) => s.id as string)
      )
      .limit(5000);
    surveyItems = ((data as unknown as Record<string, any>[]) || []).sort(
      (a, b) => (a.sort_order ?? 0) - (b.sort_order ?? 0)
    );
  }

  const routine = routineRes.data
    ? {
        name: (routineRes.data as any).name as string,
        version: (routineRes.data as any).version as string,
        status: (routineRes.data as any).status as string,
        sections: ((routineRes.data as any).sections as Record<string, string>) || {},
      }
    : null;

  return {
    projectName: (projectRes.data as { name?: string } | null)?.name ?? null,
    routine,
    surveys,
    surveyItems,
    submissions,
    riskConstruction: risks.filter((r) => r.phase !== "bruksskede"),
    riskUse: risks.filter((r) => r.phase === "bruksskede"),
    deferrals: deferralsRes.count ?? 0,
  };
};

/** Automatisk sammanräkning som projektören kan justera innan intyget skrivs ut. */
export const computeDeliveryCounts = (
  data: DesignerDeliveryData
): DesignerCertificateCounts => {
  const assessed = data.surveyItems.filter((i) => i.status !== "not_assessed");
  const risky = assessed.filter((i) => i.status === "risk");
  const noRisk = assessed.filter((i) => i.status === "no_risk");
  const residualConstruction = data.submissions.filter((s) =>
    String(s.residual_risks_construction ?? "").trim()
  ).length;
  const residualUse = data.submissions.filter((s) =>
    String(s.residual_risks_use ?? "").trim()
  ).length;

  return {
    identified: risky.length + residualConstruction + residualUse,
    designedOut: noRisk.length,
    residualConstruction: risky.length + residualConstruction,
    residualUse,
    deferred: data.deferrals,
  };
};

export const deliveryContents = (
  data: DesignerDeliveryData
): DesignerCertificateContent[] => [
  {
    docNumber: "18",
    title: "Projektörens arbetsmiljörutin",
    count: data.routine ? 1 : 0,
    note: data.routine ? `Version ${data.routine.version}` : "Saknas",
  },
  {
    docNumber: "10",
    title: "Arbetsområdesutredning",
    count: data.surveys.length,
    note: `${data.surveyItems.length} kontrollpunkter`,
  },
  {
    docNumber: "16",
    title: "Projektörsinlämning – kvarvarande risker",
    count: data.submissions.length,
    note: data.submissions.map((s) => s.discipline).join(", ") || "–",
  },
  {
    docNumber: "30b",
    title: "Risksammanställning utförande",
    count: data.riskConstruction.length,
  },
  {
    docNumber: "31",
    title: "Kvarvarande bruksrisker",
    count: data.riskUse.length,
  },
];

const READ_ME = (projectName: string | null, code: string | null) =>
  [
    "PROJEKTÖRSLEVERANS – ARBETSMILJÖ",
    "Byggarbetsmiljökontrollen",
    "",
    `Projekt: ${projectName ?? "Namnlöst projekt"}`,
    `Skapad: ${new Date().toLocaleString("sv-SE")}`,
    code ? `Verifieringskod leveransintyg: ${code}` : "",
    "",
    "SÅ HÄR LÄSER DU PAKETET",
    "1. Dok 19 Leveransintyg – börja här. Visar vad som ingår, sammanräkning av risker",
    "   och projektörens försäkran enligt AFS 2023:3 kap. 2.",
    "2. Dok 18 Projektörens arbetsmiljörutin – hur arbetet har bedrivits.",
    "3. Dok 10 Arbetsområdesutredning – förhållandena på plats.",
    "4. Dok 16 Projektörsinlämning – kvarvarande risker per disciplin.",
    "5. Dok 30b och Dok 31 – risksammanställning för byggskede respektive bruksskede.",
    "",
    "VAD MOTTAGAREN BÖR GÖRA",
    "Byggarbetsmiljösamordnaren för planering och projektering (Bas-P) för in de",
    "kvarvarande riskerna i projektets risksammanställning och arbetsmiljöplan.",
    "Vid komplettering lämnas en ny version av hela paketet.",
    "",
    "Varje Word-fil är versionsmärkt och har en verifieringskod som kan kontrolleras",
    "på arbetsmiljokontrollen.se/verifiera.",
    "",
    "LÄSVY UTAN KONTO",
    code
      ? `Hela leveransen kan även läsas på arbetsmiljokontrollen.se/leverans/${code}`
      : "Hela leveransen kan även läsas på arbetsmiljokontrollen.se/leverans med koden ovan.",
  ]
    .filter(Boolean)
    .join("\n");

export interface DeliveryProgress {
  current: number;
  total: number;
  label: string;
}

export interface DeliveryOptions {
  certificate: DesignerCertificateDoc;
  counts: DesignerCertificateCounts;
  onProgress?: (progress: DeliveryProgress) => void;
}

const triggerDownload = (blob: Blob, filename: string) => {
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
};

const defaultSections = () =>
  Object.fromEntries(DESIGNER_ROUTINE_SECTIONS.map((s) => [s.key, s.template])) as Record<
    string,
    string
  >;

/** Bygger och laddar ner hela leveranspaketet som ZIP. */
export const downloadDesignerDelivery = async (
  projectId: string,
  options: DeliveryOptions
): Promise<{ fileCount: number; failed: string[] }> => {
  const data = await collectDesignerDelivery(projectId);
  const { certificate, counts, onProgress } = options;
  const contents = deliveryContents(data);
  const failed: string[] = [];
  const zip = new JSZip();
  let code: string | null = null;

  const steps: { label: string; run: () => Promise<void> }[] = [
    {
      label: "Leveransintyg (Dok 19)",
      run: async () => {
        code = await generateDesignerCertificateWordDocument(
          certificate,
          contents,
          counts,
          data.projectName ?? undefined
        );
      },
    },
    {
      label: "Projektörens arbetsmiljörutin (Dok 18)",
      run: async () => {
        await generateDesignerRoutineWordDocument(
          data.routine ?? {
            name: "Projektörens arbetsmiljörutin",
            version: "1.0",
            status: "utkast",
            sections: defaultSections(),
          },
          data.projectName ?? undefined
        );
      },
    },
    {
      label: "Arbetsområdesutredning (Dok 10)",
      run: async () => {
        for (const survey of data.surveys) {
          await generateWorkAreaSurveyWordDocument(
            {
              area_name: survey.area_name,
              delimitation: survey.delimitation ?? null,
              adjacent_activity: survey.adjacent_activity ?? null,
              notes: survey.notes ?? null,
              status: survey.status ?? "draft",
              items: data.surveyItems
                .filter((i) => i.survey_id === survey.id)
                .map((i) => ({
                  category: i.category,
                  control_point: i.control_point,
                  legal_reference: i.legal_reference ?? null,
                  status: i.status,
                  comment: i.comment ?? null,
                  responsible: i.responsible ?? null,
                })),
            },
            data.projectName ?? undefined
          );
        }
      },
    },
    {
      label: "Projektörsinlämningar (Dok 16)",
      run: async () => {
        for (const s of data.submissions) {
          await generateDesignerSubmissionWordDocument(
            {
              discipline: s.discipline,
              company: s.company ?? null,
              contact_name: s.contact_name ?? null,
              contact_email: s.contact_email ?? null,
              status: s.status,
              residual_risks_construction: s.residual_risks_construction ?? null,
              residual_risks_use: s.residual_risks_use ?? null,
              assumptions: s.assumptions ?? null,
              basp_comment: s.basp_comment ?? null,
              submitted_at: s.submitted_at ?? null,
            },
            data.projectName ?? undefined
          );
        }
      },
    },
    {
      label: "Risksammanställningar (Dok 30b och 31)",
      run: async () => {
        for (const r of [...data.riskConstruction, ...data.riskUse]) {
          const payload = {
            ...((r.data as Record<string, unknown>) || {}),
            projektNamn: (r.data as any)?.projektNamn || data.projectName || "",
            assessmentId: r.assessment_id,
            svar: (r.data as any)?.svar || {},
          } as unknown as RiskbedomningData;
          await generateRisksammanstallningWord(
            payload,
            getAllQuestionsForExport(payload, fragorData)
          );
        }
      },
    },
  ];

  let fileCount = 0;
  for (let i = 0; i < steps.length; i += 1) {
    const step = steps[i];
    onProgress?.({ current: i, total: steps.length, label: step.label });
    beginExportCapture();
    let captured: CapturedExportFile[] = [];
    try {
      await step.run();
    } catch {
      failed.push(step.label);
    } finally {
      captured = endExportCapture();
    }
    captured.forEach((file) => {
      zip.file(`${String(i + 1).padStart(2, "0")}_${file.filename}`, file.blob);
      fileCount += 1;
    });
  }

  zip.file("LAS_MIG.txt", READ_ME(data.projectName, code));

  onProgress?.({ current: steps.length, total: steps.length, label: "Packar ZIP…" });
  const blob = await zip.generateAsync({ type: "blob" });
  const safeProject = (data.projectName || "projekt").replace(/[^\p{L}\p{N}_-]+/gu, "_");
  triggerDownload(blob, `Projektorsleverans_${safeProject}.zip`);

  return { fileCount, failed };
};
